import React, {Profiler, useContext, useMemo } from 'react';

import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { QueryProvider, QueryContext } from '@thd-nucleus/data-sources';
import { RentalRedirector } from '@thd-nucleus/app-render';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { MediaGalleryDesktopHorizontal, MediaGalleryMobile } from '@thd-olt-component-react/media-gallery';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ProductOverviewDesktop, ProductOverviewMobile } from '@thd-olt-component-react/product-overview';
import { RentalPrice } from '@thd-olt-component-react/rental-price';
import { RentalFulfillment } from '@thd-olt-component-react/rental-fulfillment';
import { SalientPoints, SalientListItem } from '@thd-olt-component-react/salient-points';
import { Specifications } from '@thd-olt-component-react/specifications';
import { Metadata } from '@thd-olt-component-react/metadata';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { getConfigLists, useFeatureCheck } from '../helpers';

import { ProductMetadata } from '../components/ProductMetadata';

import '../styles/pages/Product.scss';

export const Product = () => {
  declareContexts([QueryContext]);

  const { channel } = useContext(ExperienceContext);
  const { itemId } = useParams();
  const isMobile = channel === 'mobile';
  const localizedStore = useStore({ online: false });

  const {
    blacklistedItems = [],
    rentalClearTools = [],
    deliveryOnlyItemIds = [],
    pricingGraphQlEnabled = false,
    fulfillmentGraphQlEnabled = false,
    rodfsEnabled = false
  } = getConfigLists();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const MediaGalleryComponent = isMobile ? MediaGalleryMobile : MediaGalleryDesktopHorizontal;
  const ProductOverviewComponent = isMobile ? ProductOverviewMobile : ProductOverviewDesktop;
  const galleryPriorityProps = isMobile 
    ? { firstItemFetchPriority: 'high' } 
    : { mainImageFetchPriority: 'high' };
  const isFulfillmentGraphQlEnabled = useFeatureCheck(
    fulfillmentGraphQlEnabled,
    localizedStore?.storeId?.padStart(4, '0')
  );
  const isPricingGraphQlEnabled = useFeatureCheck(pricingGraphQlEnabled, localizedStore?.storeId?.padStart(4, '0'));
  const isRodfsEnabled = useFeatureCheck(rodfsEnabled, localizedStore?.storeId?.padStart(4, '0'));

  const showReserveNowButton = useMemo(() => !blacklistedItems.includes(itemId), [blacklistedItems, itemId]);
  const showClearBullet = useMemo(() => rentalClearTools.includes(itemId), [rentalClearTools, itemId]);
  const isDeliveryOnlyItem = useMemo(() => deliveryOnlyItemIds.includes(itemId), [deliveryOnlyItemIds, itemId]);

  const handleStartReservationButtonClick = () => {
    if (itemId) {
      const reservationsPath = pathname.replace('/p/rental', '/p/rental/reservations');
      navigate(reservationsPath);
    }
  };

  const handleStartDeliveryButtonClick = () => {
    if (isRodfsEnabled) {
      const deliveryPath = pathname.replace('/p/rental', '/p/rental/delivery');
      navigate(deliveryPath);
    } else {
      window.location.href = 'https://www.compactpowerrents.com/onsite-delivery/';
    }
  };

  return (
    <QueryProvider
      defaultVariables={{ dataSource: 'catalog', fetchPolicy: 'cache-first', skipKPF: true }}
      cacheKey={`rental-product-${isMobile ? 'mobile' : 'desktop'}`}
    >
      <Row className="isBound">
        <ErrorBoundary name="redirector">
          <RentalRedirector />
        </ErrorBoundary>
        <ErrorBoundary name="product-metadata">
          <Metadata>
            <ProductMetadata itemId={itemId} />
          </Metadata>
        </ErrorBoundary>
        <ErrorBoundary name="breadcrumbs">
          <Col>
            <Breadcrumbs
              itemId={itemId}
              showInternet={isMobile}
              shouldLastCrumbBeLinkableOverride={false}
            />
          </Col>
        </ErrorBoundary>
        <ErrorBoundary name="zone-a">
          <Col nopadding={channel === 'desktop'}>
            <Row>
              <Col sm="7">
                <div className="component">
                  {isMobile && (
                    <ErrorBoundary name="product-details">
                      <ProductDetails itemId={itemId}>
                        <div className="product-details__badge-title--wrapper">
                          <ProductDetails.Badge itemId={itemId} />
                          <ProductDetails.Title />
                          <ProductDetails.BrandCollection showCollection />
                        </div>
                      </ProductDetails>
                    </ErrorBoundary>
                  )}
                  <ErrorBoundary name="media-gallery">
                    <Col>
                      <MediaGalleryComponent 
                        itemId={itemId}
                        {...galleryPriorityProps}
                      />
                    </Col>
                  </ErrorBoundary>
                  {!isMobile && (
                    <ErrorBoundary name="product-infobar">
                      <Row>
                        <Col className="u__text-align--center">
                          <ProductInfoBar itemId={itemId} />
                        </Col>
                      </Row>
                    </ErrorBoundary>
                  )}
                </div>
              </Col>
              <Col sm="5">
                <Row>
                  <div className="component">
                    {!isMobile && (
                      <>
                        <ErrorBoundary name="product-details">
                          <Col>
                            <ProductDetails itemId={itemId}>
                              <div className="product-details__badge-title--wrapper">
                                <ProductDetails.Badge itemId={itemId} />
                                <ProductDetails.Title />
                                <ProductDetails.BrandCollection showCollection />
                              </div>
                            </ProductDetails>
                          </Col>
                        </ErrorBoundary>
                        <ErrorBoundary name="salient-points">
                          <Col>
                            <SalientPoints itemId={itemId} max={3}>
                              <SalientListItem>
                                <a className="u__default-link" href="#product-overview">
                                  See More Details
                                </a>
                              </SalientListItem>
                            </SalientPoints>
                          </Col>
                        </ErrorBoundary>
                      </>
                    )}
                    <ErrorBoundary name="rental-price">
                      <Col>
                        <RentalPrice
                          itemId={itemId}
                          isRentalClearTool={showClearBullet}
                          graphQlEnabled={isPricingGraphQlEnabled}
                        />
                      </Col>
                    </ErrorBoundary>
                    <ErrorBoundary name="rental-fulfillment">
                      <Col>
                        <RentalFulfillment
                          itemId={itemId}
                          deliveryOnly={isDeliveryOnlyItem}
                          showReserveNowButton={showReserveNowButton}
                          handleButtonClick={handleStartReservationButtonClick}
                          handleDeliveryButtonClick={handleStartDeliveryButtonClick}
                          graphQlEnabled={isFulfillmentGraphQlEnabled}
                        />
                      </Col>
                    </ErrorBoundary>
                  </div>
                </Row>
              </Col>
            </Row>
          </Col>
        </ErrorBoundary>
        <ErrorBoundary name="product-overview">
          <div id="product-overview" className="component">
            <Col>
              <ProductOverviewComponent itemId={itemId} />
            </Col>
          </div>
        </ErrorBoundary>
        <ErrorBoundary name="specifications">
          <div className="component">
            <Col>
              <Specifications itemId={itemId} />
            </Col>
          </div>
        </ErrorBoundary>
        <ErrorBoundary name="emt-links">
          <EmtLinks
            path={pathname}
            layoutGroup={{
              tag: Row,
            }}
            dynamic={{
              pageType: 'rental-pip',
            }}
            hydrator={{
              className: 'grid',
            }}
          />
        </ErrorBoundary>
      </Row>
    </QueryProvider>
  );
};

Product.displayName = 'RentalProductPage';
Product.propTypes = {};
Product.defaultProps = {};
